import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useUpdateStore } from '@/features/stores';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { displayTimeZone } from '@/shared/store/store-context';

import {
  fromPinterestSettings,
  pinterestSettingsSchema,
  toPinterestSettingsPatch,
  type PinterestSettingsForm as FormValues,
} from '../schemas/pinterest-settings-schema';

const mapField = (field: string) => field.replace(/^settings\.social\.pinterest\./, '');
const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'dailyCap',
  'minSpacingMinutes',
  'utmTagging',
  'catalogProcessingTime',
]);

export interface PinterestSettingsFormProps {
  store: Store;
  readOnly: boolean;
}

/** `settings.social.pinterest`, saved through the store PATCH (needs `settings.write`). */
export function PinterestSettingsForm({ store, readOnly }: PinterestSettingsFormProps) {
  const { t } = useTranslation(['social', 'common']);
  const update = useUpdateStore(store.id, 'social:toasts.settingsSaved');
  const message = useErrorMessage();
  const form = useForm<FormValues>({
    resolver: useI18nResolver(pinterestSettingsSchema),
    defaultValues: fromPinterestSettings(store.settings.social.pinterest),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toPinterestSettingsPatch(values));
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  const serverError = form.formState.errors.root?.server?.message;

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-6">
        {serverError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        ) : null}
        <FormSection
          title={t('social:settings.publishing.title')}
          description={t('social:settings.publishing.description')}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="dailyCap"
              render={({ field }) => (
                <FormItem data-tour="social.pinterest.settings.cap">
                  <FormLabel>{t('social:settings.dailyCap')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={100}
                      disabled={readOnly}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('social:settings.dailyCapHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="minSpacingMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('social:settings.minSpacing')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={1440}
                      disabled={readOnly}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('social:settings.minSpacingHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>
        <FormSection
          title={t('social:settings.tracking.title')}
          description={t('social:settings.tracking.description')}
        >
          <FormField
            control={form.control}
            name="utmTagging"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start gap-3">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={readOnly}
                  />
                </FormControl>
                <div className="flex flex-col gap-1">
                  <FormLabel>{t('social:settings.utmTagging')}</FormLabel>
                  <FormDescription>{t('social:settings.utmTaggingHint')}</FormDescription>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </FormSection>
        <FormSection
          title={t('social:settings.catalog.title')}
          description={t('social:settings.catalog.description')}
        >
          <FormField
            control={form.control}
            name="catalogProcessingTime"
            render={({ field }) => (
              <FormItem className="sm:max-w-xs">
                <FormLabel>{t('social:settings.processingTime')}</FormLabel>
                <FormControl>
                  <TimePicker {...field} disabled={readOnly} />
                </FormControl>
                <FormDescription>
                  {t('social:settings.processingTimeHint', {
                    zone: displayTimeZone(store.timezone),
                  })}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>
        {readOnly ? null : (
          <div>
            <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
              {update.isPending ? t('common:actions.saving') : t('social:settings.submit')}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
